/* eslint-disable consistent-return */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable camelcase */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY } from '../sharedQueries';
import { walletCurrenciesTypes } from '../../screens/Accounts/constants';
import { currencies, walletTypes } from '../constants';
import { USER_CREATE_WALLET } from '../../screens/Accounts/mutations';
import { WALLETS_ALL } from '../../screens/Accounts/queries';
import { selectConnectFinancialCustodyWallets } from '../custodyProvider';
import { useLedgerAccounts, useLedgerSupportedCurrencies } from './ledgerSDKHooks';
import useIsConnectFinancialCustodyUser from './custodyUser';

const { REACT_APP_IS_LEDGER } = process.env;
const isLedgerFiltered = REACT_APP_IS_LEDGER === 'true';

// ------ Filtered queries ----

export const useLedgerFilteredCurrencies = (queryName, params) => {
  const { data, isLoading, refetch, isFetching } = useQuery(queryName, params);
  const {
    data: ledgerCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorCurrencies,
  } = useLedgerSupportedCurrencies();

  const [result, setResult] = useState(null);

  useEffect(() => {
    const _result = {};
    if (isLedgerFiltered && data && ledgerCurrencies && !isLoadingCurrencies && !errorCurrencies) {
      const ledgerCoins = ledgerCurrencies.map((currency) => currency.ticker);
      Object.keys(data).forEach((coin) => {
        if (data[coin].currency_type === walletTypes.FIAT) {
          _result[coin] = data[coin];
        } else if (ledgerCoins.includes(coin.toUpperCase())) {
          _result[coin] = data[coin];
        }
      });
      setResult(_result);
    } else {
      setResult(data);
    }
  }, [data, ledgerCurrencies, isLoadingCurrencies, errorCurrencies]);

  return { data: result, isLoading, refetch, isFetching };
};

export const useLedgerFilteredAllowedCreationCurrencies = (queryName, params) => {
  const { data, isLoading, refetch, isFetching } = useQuery(queryName, params);
  const { data: wallets } = useQuery(WALLETS_ALL);
  const {
    data: ledgerCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorCurrencies,
  } = useLedgerSupportedCurrencies();

  const [ledgerAccounts] = useLedgerAccounts();

  const ledgerCoins = useMemo(() => {
    if (ledgerCurrencies && !isLoadingCurrencies && !errorCurrencies) {
      return ledgerCurrencies.map((currency) => currency.ticker);
    }
    return [];
  }, [ledgerCurrencies, isLoadingCurrencies, errorCurrencies]);

  const createdLedgerCurrencies = useMemo(() => Object.keys(ledgerAccounts), [ledgerAccounts]);
  const createdBaanxWallets = useMemo(
    () => (wallets ? wallets.map((wallet) => wallet.coin) : []),
    [wallets]
  );

  const [result, setResult] = useState({});

  useEffect(() => {
    let _result = {};
    if (isLedgerFiltered && data && wallets) {
      Object.keys(data).forEach((coin) => {
        if (data[coin].currency_type === walletTypes.FIAT) {
          _result[coin] = data[coin];
        } else if (
          ledgerCoins.includes(coin.toUpperCase()) &&
          createdLedgerCurrencies.includes(coin.toLowerCase()) &&
          !createdBaanxWallets.includes(coin.toLowerCase())
        ) {
          _result[coin] = data[coin];
        }
      });
    } else {
      _result = data;
    }

    setResult(_result);
  }, [createdBaanxWallets, createdLedgerCurrencies, data, ledgerCoins, wallets]);

  return { data: result, isLoading, refetch, isFetching };
};

export const useLedgerFilteredWallets = (queryName, params) => {
  const isConnectFinancialCustodyUser = useIsConnectFinancialCustodyUser();
  const queryParams =
    isConnectFinancialCustodyUser && params?.onSettled
      ? {
          ...params,
          onSettled: (settledData, error) =>
            params.onSettled(selectConnectFinancialCustodyWallets(settledData), error),
        }
      : params;
  const { refetch, isLoading, isFetching, data } = useQuery(queryName, queryParams);
  const { data: fiatCurrencies, isLoading: isLoadingSupportedCurrencies } = useQuery(
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
    {
      currency_type: walletCurrenciesTypes.FIAT,
    }
  );
  const [ledgerAccounts] = useLedgerAccounts();

  const wallets = useMemo(() => {
    const visibleWallets = data?.filter((wallet) => {
      const shouldFilterWallet =
        wallet.wallet_type?.toLowerCase() === walletTypes.CRYPTO && isLedgerFiltered;

      if (shouldFilterWallet) {
        const ledgerAccount = ledgerAccounts[wallet.coin.toLowerCase()];
        return !!ledgerAccount;
      }

      if (
        wallet.wallet_type?.toLowerCase() === walletTypes.CRYPTO &&
        fiatCurrencies &&
        !!fiatCurrencies[wallet.coin]?.fiat_currency_peg
      ) {
        wallet.wallet_type = walletTypes.FIAT;
      }

      return true;
    });

    return isConnectFinancialCustodyUser
      ? selectConnectFinancialCustodyWallets(visibleWallets)
      : visibleWallets;
  }, [data, ledgerAccounts, fiatCurrencies, isConnectFinancialCustodyUser]);

  return {
    refetch,
    isLoading: isLoading || isLoadingSupportedCurrencies,
    isFetching,
    data: wallets,
  };
};

export const useSyncLedgerBaanxWallets = (ledgerAccounts, wallets, refreshWallets) => {
  const { mutate: createWallets } = useMutation(USER_CREATE_WALLET, {
    onSettled: (_, error) => {
      if (!error) {
        refreshWallets();
      }
    },
  });

  useEffect(() => {
    if (ledgerAccounts && Object.keys(ledgerAccounts).length > 0 && wallets && wallets.length > 0) {
      const walletsCoins = wallets.map((wallet) => wallet.coin.toLowerCase());
      const uncreatedWallets = [];

      Object.keys(ledgerAccounts).forEach((ledgerAccountCoin) => {
        if (!walletsCoins.includes(ledgerAccountCoin)) {
          uncreatedWallets.push(ledgerAccountCoin.toLowerCase());
        }
      });

      if (uncreatedWallets.length > 0) {
        createWallets({ wallets: uncreatedWallets });
      }
    }
  }, [wallets, ledgerAccounts]);
};

export const useLedgerFilteredLinkableWallets = (queryName, params) => {
  const { refetch, isLoading, isFetching, data } = useQuery(queryName, params);
  const [ledgerAccounts] = useLedgerAccounts();
  const wallets = useMemo(
    () =>
      data?.filter((wallet) => {
        const shouldFilterWallet =
          wallet?.wallet_type?.toLowerCase() === walletTypes.CRYPTO && isLedgerFiltered;

        if (shouldFilterWallet) {
          const ledgerAccount = ledgerAccounts[wallet.coin.toLowerCase()];
          return !!ledgerAccount;
        }

        return true;
      }),
    [data, ledgerAccounts]
  );

  return {
    refetch,
    isLoading,
    isFetching,
    data: wallets,
  };
};

export const useLedgerAccountForWallet = (wallet) => {
  const [ledgerAccounts] = useLedgerAccounts();
  const { data: supportedCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  return useMemo(() => {
    if (!ledgerAccounts) {
      return null;
    }
    const coin = wallet?.coin;
    const ethAccount = ledgerAccounts[currencies.ETH.icon.toLowerCase()];
    if (supportedCurrencies?.[coin]?.is_erc20_token && ethAccount) {
      return ethAccount;
    }
    return ledgerAccounts[coin];
  }, [ledgerAccounts, supportedCurrencies, wallet?.coin]);
};
